import { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth0Tokens } from '../../hooks/useAuth0Tokens';
import SyncButton from './SyncButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import {
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Chip,
} from '@mui/material';

const DATAGRID_SX = {
  border: 0,
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: 'background.subtle',
    borderBottom: 1,
    borderColor: 'divider',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    color: 'text.secondary',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .MuiDataGrid-cell': {
    color: 'text.primary',
    borderColor: 'divider',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-footerContainer': {
    bgcolor: 'background.subtle',
    borderTop: 1,
    borderColor: 'divider',
  },
  '& .MuiDataGrid-row:hover': {
    bgcolor: 'action.hover',
  },
};

const SPIN_SX = {
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
};

const OktaViewModal = ({ isOpen, onClose, orgs, setOrgs, onRefresh }) => {
  const { getTokens } = useAuth0Tokens();
  const [selectedRows, setSelectedRows] = useState([]);
  const [syncingOrg, setSyncingOrg] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  if (!isOpen) return null;

  const handleSync = async (orgName) => {
    try {
      setSyncingOrg(orgName);
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
      const { apiToken } = await getTokens();

      // Find the integration by orgName to get its ID
      const integration = orgs.find(org => org.name === orgName);
      if (!integration) {
        throw new Error('Integration not found');
      }

      const mutation = `
        mutation SyncIntegration($id: ID!) {
          syncIntegration(id: $id) {
            success
            message
            status
          }
        }
      `;

      const variables = {
        id: integration.id
      };

      console.log('Triggering sync for integration:', { id: integration.id, orgName });

      // Trigger async sync
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`
        },
        body: JSON.stringify({ query: mutation, variables })
      });

      const responseData = await response.json();
      console.log('Sync trigger response:', responseData);

      if (responseData.errors) {
        throw new Error(responseData.errors[0]?.message || 'GraphQL error occurred');
      }

      const result = responseData.data?.syncIntegration;
      if (!result || !result.success) {
        throw new Error(result?.message || 'Failed to start sync');
      }

      console.log('Sync started:', result);

      // Poll for sync status
      await pollSyncStatus(integration.id, apiToken, graphqlEndpoint);

      // Refresh the data after successful sync
      await onRefresh();

    } catch (error) {
      console.error('Error syncing integration:', error);
      alert(`Failed to sync integration: ${error.message}`);
    } finally {
      setSyncingOrg(null);
    }
  };

  const pollSyncStatus = async (integrationId, apiToken, graphqlEndpoint) => {
    const query = `
      query GetSyncStatus($id: ID!) {
        getSyncStatus(id: $id) {
          status
          message
          itemsProcessed
          error
        }
      }
    `;

    const maxAttempts = 60; // Poll for up to 2 minutes
    const pollInterval = 2000; // Poll every 2 seconds

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiToken}`
          },
          body: JSON.stringify({
            query,
            variables: { id: integrationId }
          })
        });

        const responseData = await response.json();

        if (responseData.errors) {
          console.error('Error checking sync status:', responseData.errors);
          continue;
        }

        const status = responseData.data?.getSyncStatus;
        console.log(`Sync status (attempt ${attempt + 1}):`, status);

        if (status?.status === 'COMPLETED') {
          console.log(`Sync completed successfully! Items processed: ${status.itemsProcessed}`);
          return;
        } else if (status?.status === 'FAILED') {
          throw new Error(status.error || 'Sync failed');
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (error) {
        console.error('Error polling sync status:', error);
        throw error;
      }
    }

    // Timeout
    throw new Error('Sync timeout - taking longer than expected');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const columns = [
    {
      field: 'index',
      headerName: '#',
      width: 60,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => {
        try {
          return (params.api && typeof params.api.getRowIndexRelativeToVisibleRows === 'function')
            ? params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
            : (params.row.index || 0);
        } catch (e) {
          return params.row.index || 0;
        }
      }
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
      flex: 0.5
    },
    {
      field: 'org_name',
      headerName: 'Name',
      width: 120,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'date',
      headerName: 'Last Synced',
      width: 120,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'Active' ? 'success' : params.value === 'Integrating...' ? 'warning' : 'error'}
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      field: 'comment',
      headerName: 'Comments',
      width: 250,
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <SyncButton
          onSync={() => handleSync(params.row.org_name)}
          disabled={syncingOrg === params.row.org_name}
        />
      )
    }
  ];

  const rows = orgs.map((org) => ({
    id: org.id,
    org_name: org.name,
    date: org.date,
    status: org.status,
    comment: org.comment
  }));

  const paginationModel = { page: 0, pageSize: 5 };

  const handleDelete = async () => {
    if (selectedRows.length === 0) return;

    try {
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
      const { apiToken } = await getTokens();

      // Get the selected integrations to delete
      const integrationsToDelete = orgs.filter(org => selectedRows.includes(org.id));

      // Delete each integration using GraphQL mutation
      const deletePromises = integrationsToDelete.map(async (integration) => {
        const mutation = `
          mutation DeleteIntegration($input: DeleteIntegrationInput!) {
            deleteIntegration(input: $input) {
              success
              message
              integrationId
              errors
            }
          }
        `;

        const variables = {
          input: {
            id: integration.id,
            integrationType: "okta",
            orgName: integration.name
          }
        };

        console.log('Deleting integration:', { id: integration.id, orgName: integration.name });

        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiToken}`
          },
          body: JSON.stringify({ query: mutation, variables })
        });

        const responseData = await response.json();
        console.log('Delete response:', responseData);

        if (responseData.errors) {
          throw new Error(responseData.errors[0]?.message || 'GraphQL error occurred');
        }

        const result = responseData.data?.deleteIntegration;
        if (!result || !result.success) {
          throw new Error(result?.message || result?.errors?.[0] || 'Failed to delete integration');
        }

        return result;
      });

      // Wait for all deletes to complete
      await Promise.all(deletePromises);

      // Update local state - remove deleted integrations
      const updatedOrgs = orgs.filter(org => !selectedRows.includes(org.id));
      setOrgs(updatedOrgs);
      setSelectedRows([]);

      console.log('Successfully deleted integrations');
    } catch (error) {
      console.error('Error deleting integrations:', error);
      alert(`Failed to delete integration: ${error.message}`);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Linked Tenants</Typography>
          <IconButton onClick={handleRefresh} disabled={isRefreshing} size="small" color="primary">
            <RefreshIcon sx={isRefreshing ? SPIN_SX : {}} />
          </IconButton>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{ pagination: { paginationModel } }}
          pageSizeOptions={[5, 10]}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          slots={{
            toolbar: () => (
              selectedRows.length > 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Button variant="contained" color="error" size="small" onClick={handleDelete}>
                    Delete selected ({selectedRows.length})
                  </Button>
                </Box>
              ) : null
            )
          }}
          sx={DATAGRID_SX}
          autoHeight
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 100}
        />
      </DialogContent>
    </Dialog>
  );
};

export default OktaViewModal;
